import React, { useEffect } from 'react'
import { Grid } from '@mui/material'
import { array, object, string } from 'yup'
import { useQueryClient } from '@tanstack/react-query'

import {
  Form,
  FormSchemaMessages,
  FormSchemaTests,
  InputNormalizers,
  SubmitButton,
  TextFieldControl,
} from '../../../../components/form'
import { NewB2BFormState, NewC2BFormState, QrFormProps } from '../../qrs.types.ts'
import { Dialog, useSnackbarContext } from '../../../../components'
import { FormControl } from '../../../../components/form/controls/form-control.tsx'
import { OfdFields } from '../../../../components/ofd-fields'
import { useMutateRegisterC2B } from '../../../../services'
import { omitRequestParams, preventHandler } from '../../../../utils.ts'
import { RegisterC2BQrParams } from '../../../../types.ts'

import { MerchantField } from '../form-fields/merchant-field.tsx'
import { useAppSelector } from '@/stores/hooks.ts'

const C2B_FORM_NAME = 'newC2BForm'

const newC2BValidationSchema = (withOfd = false) =>
  object<NewC2BFormState>({
    merchantId: string().required(FormSchemaMessages.required),
    purpose: string().required(FormSchemaMessages.required),
    amount: string().test(FormSchemaTests.isWithinRange(10, 1000000)),
    reference: string(),
    expireMin: string().test(FormSchemaTests.isWithinRange(5, 129600)),
    email: withOfd ? string().required(FormSchemaMessages.required) : string(),
    // валидация каждой позиции происходит в ofd-position-dialog.tsx
    positions: withOfd ? array().required('Необходимо добавить позиции') : array(),
  })

export const NewC2BForm = ({ open, onClose }: QrFormProps): React.ReactElement => {
  const tspList = useAppSelector((state) => state.user.user?.tspList ?? [])
  const ofdPermission = useAppSelector((state) => state.user.user?.permissions?.ofd)

  const qc = useQueryClient()
  const { mutateAsync, status, isPending, data, reset } = useMutateRegisterC2B()
  const { addSnack } = useSnackbarContext()

  const handleCreateC2BSubmit = async ({ positions = [], amount, expireMin, ...formState }: NewC2BFormState) => {
    await preventHandler(
      mutateAsync,
      isPending
    )(
      omitRequestParams<RegisterC2BQrParams>({
        ...formState,
        amount: Number(amount),
        expireMin: Number(expireMin),
        positions: positions.map(({ id, ...position }) => position),
      })
    )
  }

  useEffect(() => {
    switch (status) {
      case 'success': {
        const response = data?.data
        const isSuccess = response.state === 'REGISTERED'
        const errorMessage =
          response.state === 'REJECTED' && 'errorMessage' in response
            ? response?.errorMessage ?? 'Произошла ошибка при создании QR, попробуйте ещё раз'
            : ''

        addSnack({
          message: isSuccess ? 'Заявка на создание QR C2B успешно отправлена' : errorMessage,
          severity: isSuccess ? 'success' : 'error',
          timeout: isSuccess ? 6000 : 3000,
          closeIcon: false,
        })

        if (isSuccess) {
          qc.invalidateQueries({ queryKey: ['C2B_list'] })
          onClose()
          reset()
        }

        break
      }

      case 'error': {
        addSnack({
          message: 'Произошла непредвиденная ошибка, попробуйте ещё раз',
          severity: 'error',
          timeout: 3000,
          closeIcon: false,
        })
        break
      }

      default:
        break
    }
  }, [status])

  return (
    <Form<NewC2BFormState>
      name={C2B_FORM_NAME}
      onSubmit={handleCreateC2BSubmit}
      validationSchema={newC2BValidationSchema(ofdPermission)}
      initialValues={{
        merchantId: tspList[0]?.merchantId,
        qrcType: '02',
      }}
    >
      <Dialog
        title="Заявка на C2B QR"
        maxWidth="md"
        open={open}
        onClose={onClose}
        content={
          <Grid container flexDirection="column" spacing={2}>
            <Grid item xs={12}>
              <MerchantField />
            </Grid>
            <Grid item xs={12}>
              <TextFieldControl<NewC2BFormState> name="reference" fullWidth label="№ заказа в магазине" />
            </Grid>
            <Grid item xs={12}>
              <TextFieldControl<NewC2BFormState>
                name="amount"
                fullWidth
                normalize={InputNormalizers.positiveNumbersOnly()}
                label="Сумма платежа"
              />
            </Grid>
            <Grid item xs={12}>
              <TextFieldControl<NewC2BFormState> name="purpose" fullWidth label="Назначение платежа" />
            </Grid>
            <Grid item xs={12}>
              <TextFieldControl<NewB2BFormState>
                name="expireMin"
                fullWidth
                label="Время жизни ссылки (мин)"
                helperText="Введите значение от 5 до 129600 (90 дней). Время по умолчанию: 4320 (трое суток)"
              />
            </Grid>
            {ofdPermission && (
              <Grid item xs={12}>
                <FormControl name="positions" component={OfdFields} />
              </Grid>
            )}
          </Grid>
        }
        actions={<SubmitButton form={C2B_FORM_NAME}>Отправить заявку</SubmitButton>}
      />
    </Form>
  )
}
